import React from "react";
import { Lock } from "lucide-react";
import { Badge } from "../Primitives";
import { relTime } from "../apikeys/shared";
import { actorName } from "./util";
import { cn } from "../../lib/utils";

const LEVEL = { high: "critical", medium: "warning", low: "neutral" };

export function Toggle({ on, disabled, onClick, testid, label }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} data-testid={testid}
            disabled={disabled} onClick={onClick}
            className={cn("relative w-10 h-5 rounded-full border transition-colors shrink-0 disabled:cursor-not-allowed disabled:opacity-50",
              on ? "bg-success/80 border-success" : "bg-surface-2 border-border-strong")}>
      <span className={cn("absolute top-0.5 left-0.5 w-3.5 h-3.5 rounded-full bg-white shadow transition-transform", on && "translate-x-5")} />
    </button>
  );
}

export default function ModuleRow({ mod, labelOf, onToggle }) {
  const slug = mod.path === "/" ? "home" : mod.path.replace(/^\//, "").replace(/\//g, "-");
  const last = mod.last_change;
  return (
    <div data-testid={`module-row-${slug}`} data-visible={mod.visible ? "true" : "false"}
         className="px-4 py-3.5 flex flex-col sm:flex-row items-start gap-3 sm:gap-4 border-b border-border/70 last:border-b-0 hover:bg-surface-2/40 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] font-semibold">{mod.label}</span>
          <span className="text-[10px] font-mono-tabular text-subtle">{mod.path}</span>
          <Badge status={LEVEL[mod.sensitivity_level]} testid={`module-sensitivity-${slug}`}>{mod.sensitivity_level} impact</Badge>
          {mod.locked && <Badge status="info" testid={`module-locked-${slug}`}><Lock size={9} /> locked</Badge>}
        </div>
        <div className="text-[11.5px] text-fg/85 mt-1 leading-snug">{mod.sensitivity}</div>
        <div className="text-[11px] text-muted mt-0.5 leading-snug">{mod.dependencies}</div>
        {mod.linked_from.length > 0 && (
          <div className="text-[10.5px] text-subtle mt-1">Linked from: {mod.linked_from.map(labelOf).join(" · ")}</div>
        )}
      </div>
      <div className="flex sm:flex-col items-center sm:items-end justify-between gap-1.5 shrink-0 w-full sm:w-40">
        <div className="flex items-center gap-2">
          <span data-testid={`module-state-${slug}`} className={cn("text-[10px] uppercase tracking-widest font-semibold", mod.visible ? "text-success" : "text-muted")}>
            {mod.visible ? "Visible" : "Hidden"}
          </span>
          <Toggle on={mod.visible} disabled={mod.locked} onClick={() => onToggle(mod)}
                  testid={`module-toggle-${slug}`} label={`${mod.visible ? "Hide" : "Show"} ${mod.label}`} />
        </div>
        <div className="text-[10px] text-subtle sm:text-right leading-snug order-first sm:order-none" data-testid={`module-last-change-${slug}`} title={last?.ts}>
          {last ? <>{actorName(last.actor)} · {relTime(last.ts)}</> : "No changes yet"}
        </div>
      </div>
    </div>
  );
}
